// App-side delegate for `@heidi/session/data` (D5, ARCFE-990). The hook and
// its module commands moved; this file composes the ONE app input —
// `useIsFreeUser`, feeding the injected brain transform — and keeps every
// name importers use.
import { useIsFreeUser } from '@/hooks/useIsFreeUser';

// LOAD-BEARING side-effect imports — the legacy module's own value imports,
// kept in their original order. `@/pages/api/scribeAPI` reaches
// `@/utils/scribeHTTP`, which value-imports `@/store` (scribeHTTP.ts), so
// evaluating this module initializes the store↔scribeRecordModel cycle from
// the safe direction; suites' import windows are written against that graph
// (dropping it surfaces the "Cannot access 'scribeRecordModel' before
// initialization" TDZ). Deleted with this delegate at Z2.
import '@/models/scribeRecordModel';
import '@/pages/api/scribeAPI';

import {
  ensureQueryDocumentById,
  invalidateDocumentQuery,
  useQueryDocumentById as useQueryDocumentByIdCore,
} from '@heidi/session/data';

// Keys + cached-entry type moved to `@heidi/session/contracts` (OpenSpec
// `monorepo-session-data-core` C1/C4). Imported for this file's own use and
// re-exported so existing importers keep compiling until the Z-series codemod.
import {
  GET_DEMO_DOCUMENT_BY_ID,
  GET_DOCUMENT_BY_ID,
  type QueryDocumentResponse,
} from '@heidi/session/contracts';

export {
  ensureQueryDocumentById,
  GET_DEMO_DOCUMENT_BY_ID,
  GET_DOCUMENT_BY_ID,
  invalidateDocumentQuery,
  type QueryDocumentResponse,
};

export function useQueryDocumentById<T = QueryDocumentResponse>({
  sessionId,
  documentId,
  select,
  refetchOnMount,
}: {
  sessionId: string;
  documentId: string;
  select?: (data: QueryDocumentResponse | undefined) => T;
  refetchOnMount?: boolean | 'always';
}) {
  const { isFreeUser } = useIsFreeUser();

  return useQueryDocumentByIdCore<T>({
    sessionId,
    documentId,
    isFreeUser,
    select,
    refetchOnMount,
  });
}

/** Legacy alias — the "authenticated" variant was the implementation. */
export const useQueryDocumentByIdAuthenticated = useQueryDocumentById;
